import React, { useEffect } from 'react';
import { Modal, Form, Input, InputNumber, Button } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { SankalpaLine } from '../types';
import { getLineEditFormMetadataFields, buildLineEditMetadataPayload } from '../utils/bidhiEditByTag';
import SankalpaLineBhedPanel from './SankalpaLineBhedPanel';
import BidhiMetadataTagsSelect from './bidhi/BidhiMetadataTagsSelect';

interface SankalpaLineEditModalProps {
  visible: boolean;
  line: SankalpaLine | null;
  tagOptions?: string[];
  onCancel: () => void;
  onSubmit: (
    lineId: number,
    content: string,
    order: number,
    metadata: Array<{ key: string; value: unknown }>,
  ) => void;
  onBhedsChange?: () => void;
  initialEditBhedId?: number | null;
}

const SankalpaLineEditModal: React.FC<SankalpaLineEditModalProps> = ({
  visible,
  line,
  tagOptions = [],
  onCancel,
  onSubmit,
  onBhedsChange,
  initialEditBhedId,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (line && visible) {
      const { tags, metadata: metadataArray } = getLineEditFormMetadataFields(line.metadata);
      form.setFieldsValue({
        content: line.content,
        order: line.order || 1,
        tags,
        metadata: metadataArray,
      });
    }
  }, [line, visible, form]);

  const handleSubmit = (values: any) => {
    if (line?.id) {
      const metadata = buildLineEditMetadataPayload(values.metadata || [], values.tags || []);
      onSubmit(line.id, values.content, values.order, metadata);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title="Edit Line"
      open={visible}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      width={640}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="order"
          label="Order"
          rules={[{ required: true, message: 'Please enter order' }]}
        >
          <InputNumber min={1} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="content"
          label="Content"
          rules={[{ required: true, message: 'Please enter line content' }]}
        >
          <Input.TextArea rows={4} />
        </Form.Item>

        <Form.Item name="tags" label="Tags">
          <BidhiMetadataTagsSelect tagOptions={tagOptions} />
        </Form.Item>

        <Form.List name="metadata">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <div key={key} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                  <Form.Item
                    {...restField}
                    name={[name, 'key']}
                    rules={[{ required: true, message: 'Key required' }]}
                    style={{ flex: 1, marginBottom: 0 }}
                  >
                    <Input placeholder="Key" />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'value']}
                    rules={[{ required: true, message: 'Value required' }]}
                    style={{ flex: 1, marginBottom: 0 }}
                  >
                    <Input placeholder="Value" />
                  </Form.Item>
                  <Button
                    type="text"
                    danger
                    onClick={() => remove(name)}
                    icon={<DeleteOutlined />}
                  />
                </div>
              ))}
              <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                Add Metadata
              </Button>
            </>
          )}
        </Form.List>
      </Form>

      <SankalpaLineBhedPanel
        sankalpaLineId={line?.id ?? null}
        visible={visible}
        lineContent={line?.content ?? ''}
        onBhedsChange={onBhedsChange}
        initialEditBhedId={initialEditBhedId}
      />
    </Modal>
  );
};

export default SankalpaLineEditModal;
